/*  Trainee Reports – per‑week browser
    – Entire page wrapped in a primary card
    – Individual report cards coloured by type
    – Responsive: 1·2·3·4 cols
    – Sticky back‑button on mobile
*/
import React, {
  useContext, useEffect, useState, useMemo,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, query, where, getDocs,
  doc, getDoc, Timestamp,
} from 'firebase/firestore';
import {
  format, parseISO, isValid,
  startOfWeek, endOfWeek,
} from 'date-fns';
import {
  FiCalendar, FiFileText, FiArrowDown, FiArrowUp,
  FiUser, FiClock, FiAlertTriangle, FiChevronLeft,
  FiCheckCircle, FiFolder, FiActivity, FiHash, FiEdit2,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

import { db }            from '../utils/firebaseConfig';
import { AuthContext }   from '../contexts/AuthContext';
import LoadingSpinner    from '../components/LoadingSpinner';
import defaultAvatar     from '../assets/default-avatar.svg';

/* ------------------------------------------------------------------ */
/* helpers ---------------------------------------------------------- */


const toDate = (v) => {
  if (v instanceof Date)      return v;
  if (v instanceof Timestamp) return v.toDate();
  if (typeof v === 'string')  return parseISO(v);
  return null;
};

const fmtDateTime  = (d) => (isValid(d) ? format(d, 'MMMM dd, yyyy h:mm a'): '');

const colourClasses = {
  DOR:                 'report-border-dor',
  EndOfWeekReport:     'report-border-eowr',
  OfficerSafetyReport: 'report-border-osr',
};

const shadeClasses = {
  DOR:                 'report-bg-dor',
  EndOfWeekReport:     'report-bg-eowr',
  OfficerSafetyReport: 'report-bg-osr',
};

const reportTypeBadges = {
  DOR:                 'report-badge-dor',
  EndOfWeekReport:     'report-badge-eowr',
  OfficerSafetyReport: 'report-badge-osr'
};

const ReportIcon = ({ type }) => {
  const cls = {
    DOR:                 'text-[color:var(--dor-bar)]',
    EndOfWeekReport:     'text-[color:var(--eowr-bar)]',
    OfficerSafetyReport: 'text-[color:var(--osr-bar)]',
  }[type] || '';

  return (
    type === 'EndOfWeekReport'     ? <FiCalendar     className={`shrink-0 ${cls}`} /> :
    type === 'OfficerSafetyReport' ? <FiAlertTriangle className={`shrink-0 ${cls}`} /> :
                                     <FiFileText      className={`shrink-0 ${cls}`} />
  );
};

/* ------------------------------------------------------------------ */
export default function TraineeReportsPage() {
  const { traineeId } = useParams();
  const nav           = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const [weeks, setWeeks]         = useState([]);
  const [reports, setReports]     = useState([]);
  const [selected, setSel]        = useState(null);
  const [loading, setLoad]        = useState({ weeks: true });
  const [err, setErr]             = useState(null);
  const [creatorNames, setNames]  = useState({});
  const [traineeName, setTN]      = useState('');
  const [traineeIdNumber, setTIN] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [totals, setTotals]       = useState({ dors: 0, eowrs: 0, osrs: 0 });

  /* ---------------- fetch data once ------------------------------- */
  useEffect(() => {
    if (!currentUser) { nav('/login'); return; }

    (async () => {
      try {
        const defs = [
          { col: 'dors',                field: 'date',          type: 'DOR' },
          { col: 'endOfWeekReports',    field: 'weekEndingDate',type: 'EndOfWeekReport' },
          { col: 'officerSafetyReports',field: 'reportDate',    type: 'OfficerSafetyReport' },
        ];

        const set = new Set();
        const rows = [];
        const reportCounts = { dors: 0, eowrs: 0, osrs: 0 };

        for (const { col, field, type } of defs) {
          const snap = await getDocs(query(
            collection(db, col),
            where('traineeId', '==', traineeId),
          ));

          snap.forEach((d) => {
            const dt = toDate(d.data()[field]);
            if (!isValid(dt)) return;

            const wk = startOfWeek(dt, { weekStartsOn: 1 });
            set.add(+wk);

            rows.push({ id: d.id, ...d.data(), reportType: type, reportDate: dt });
            
            // Count report types
            if (type === 'DOR') reportCounts.dors++;
            else if (type === 'EndOfWeekReport') reportCounts.eowrs++;
            else if (type === 'OfficerSafetyReport') reportCounts.osrs++;
          });
        }

        setWeeks([...set].map(t => new Date(t)).sort((a, b) => b - a));
        setReports(rows);
        setTotals(reportCounts);

        const usr = await getDoc(doc(db, 'users', traineeId));
        if (usr.exists()) {
          const userData = usr.data();
          setTN(userData.name || '');
          setTIN(userData.idNumber || '');
          setProfilePicture(userData.photoURL || null);
        }

        setErr(null);
      } catch (e) {
        console.error(e);
        setErr('Failed to fetch data.');
        toast.error('Error loading data.');
      } finally { setLoad({ weeks: false }); }
    })();
  }, [currentUser, traineeId, nav]);

  /* ---------------- derived week reports -------------------------- */
  const weekReports = useMemo(() => {
    if (!selected) return [];
    const end = endOfWeek(selected, { weekStartsOn: 1 });
    return reports
      .filter(r => r.reportDate >= selected && r.reportDate <= end)
      .sort((a, b) => {
        // End of Week Reports always come first
        if (a.reportType === 'EndOfWeekReport' && b.reportType !== 'EndOfWeekReport') return -1;
        if (b.reportType === 'EndOfWeekReport' && a.reportType !== 'EndOfWeekReport') return 1;
        
        // Then sort by date, most recent first
        return b.reportDate - a.reportDate;
      });
  }, [selected, reports]);

  /* fetch unknown creator names once */
  useEffect(() => {
    (async () => {
      const ids = [...new Set(weekReports.map(r => r.createdBy || ''))]
        .filter(id => id && !creatorNames[id]);
      if (!ids.length) return;

      const map = { ...creatorNames };
      await Promise.all(ids.map(async (id) => {
        const snap = await getDoc(doc(db, 'users', id));
        if (snap.exists()) map[id] = snap.data().name;
      }));
      setNames(map);
    })();
  }, [weekReports]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- UI bits --------------------------------------- */
  if (loading.weeks) {
    return (
      <div className="min-h-[75vh] p-4 sm:p-6 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark">
        <div className="max-w-5xl mx-auto">
          {/* Back button skeleton */}
          <div className="mb-4 flex justify-end">
            <div className="w-32 h-9 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-md animate-pulse"></div>
          </div>
          
          {/* Trainee Profile Card skeleton */}
          <div className="mb-6 p-5 rounded-xl shadow-lg bg-primary-light dark:bg-primary-dark border-2 border-secondary-light dark:border-secondary-dark">
            <div className="flex flex-row items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-md bg-secondary-light/50 dark:bg-secondary-dark/50 animate-pulse"></div>
              <div className="flex-1 min-w-0">
                <div className="h-7 w-48 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded animate-pulse mb-2"></div>
                <div className="h-5 w-32 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded animate-pulse"></div>
              </div>
            </div>
            
            {/* Report summary cards skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-3 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50">
                  <div className="h-4 w-24 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded animate-pulse mb-2"></div>
                  <div className="h-6 w-12 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Week Selector skeleton */}
          <div className="p-5 rounded-xl shadow-lg bg-primary-light dark:bg-primary-dark border-2 border-secondary-light dark:border-secondary-dark">
            <div className="h-6 w-40 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded animate-pulse mb-4"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="w-full p-3 rounded-lg bg-secondary-light/50 dark:bg-secondary-dark/50 animate-pulse h-16"></div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Loading spinner overlay */}
        <div className="fixed inset-0 flex items-center justify-center z-10 pointer-events-none">
          <LoadingSpinner />
        </div>
      </div>
    );
  }
  
  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <div className="bg-primary-light dark:bg-primary-dark p-6 rounded-xl shadow-lg border border-secondary-light dark:border-secondary-dark text-center">
          <p className="text-red-600 dark:text-red-400 mb-4 text-lg font-medium">{err}</p>
          <button 
            onClick={() => nav('/dors')} 
            className="px-6 py-3 bg-accent-light dark:bg-accent-dark hover:brightness-110 text-primary-light dark:text-primary-dark rounded-lg transition-all duration-300 font-medium"
          >
            Back to reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[75vh] p-4 sm:p-6 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-xs sm:text-sm">
      <div className="max-w-5xl mx-auto">
        
        {/* Back button - top of page for better UX */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => nav('/dors')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark rounded-md shadow-md hover:brightness-110 hover:translate-y-[-1px] transition-all duration-200"
          >
            <FiChevronLeft className="w-3.5 h-3.5" /> Back to Reports
          </button>
        </div>
        
        {/* Trainee Profile Card */}
        <div className="mb-6 p-5 rounded-xl shadow-lg bg-primary-light dark:bg-primary-dark border-2 border-secondary-light dark:border-secondary-dark hover:border-accent-light dark:hover:border-accent-dark">
          <div className="flex flex-row items-center gap-4 mb-4">
            {/* Profile Picture/Icon */}
            <div 
              className={`w-14 h-14 rounded-md overflow-hidden ${!profilePicture || profilePicture === defaultAvatar ? 'bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark' : ''} flex items-center justify-center text-2xl font-medium shadow-md`}
            >
              {profilePicture && profilePicture !== defaultAvatar ? (
                <img src={profilePicture} alt={traineeName || 'Trainee'} className="w-full h-full object-cover" />
              ) : traineeName ? (
                <span className="text-3xl">{traineeName.charAt(0).toUpperCase()}</span>
              ) : (
                <FiUser className="w-8 h-8" />
              )}
            </div>
            
            {/* Name and ID */}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold mb-0.5 truncate">
                {traineeName || 'Trainee'}
              </h1>
              {traineeIdNumber && (
                <div className="flex items-center text-sm text-text-light/70 dark:text-text-dark/70">
                  <FiHash className="w-3.5 h-3.5 mr-1" />
                  <span>{traineeIdNumber}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Reports Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <div className="report-bg-dor p-3 rounded-lg border report-border-dor shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-2 mb-1">
                <FiFileText className="w-4 h-4 text-[color:var(--dor-bar)]" />
                <span className="text-xs font-medium uppercase text-[color:var(--dor-badge-text)]">Daily Reports</span>
              </div>
              <div className="text-lg font-bold">{totals.dors}</div>
            </div>

            <div className="report-bg-eowr p-3 rounded-lg border report-border-eowr shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-2 mb-1">
                <FiCalendar className="w-4 h-4 text-[color:var(--eowr-bar)]" />
                <span className="text-xs font-medium uppercase text-[color:var(--eowr-badge-text)]">Weekly Reports</span>
              </div>
              <div className="text-lg font-bold">{totals.eowrs}</div>
            </div>

            <div className="report-bg-osr p-3 rounded-lg border report-border-osr shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-2 mb-1">
                <FiAlertTriangle className="w-4 h-4 text-[color:var(--osr-bar)]" />
                <span className="text-xs font-medium uppercase text-[color:var(--osr-badge-text)]">Safety Reports</span>
              </div>
              <div className="text-lg font-bold">{totals.osrs}</div>
            </div>
          </div>
        </div>
        
        {/* Week Selector Section */}
        <div className="p-5 rounded-xl shadow-lg bg-primary-light dark:bg-primary-dark border-2 border-secondary-light dark:border-secondary-dark hover:border-accent-light dark:hover:border-accent-dark mb-6">
          <h2 className="flex items-center gap-2 font-semibold text-base sm:text-lg mb-4 border-b border-secondary-light/30 dark:border-secondary-dark/30 pb-2">
            <FiFolder className="text-accent-light dark:text-accent-dark" />
            <span>Reports by Week</span>
          </h2>

          {weeks.length === 0 ? (
            <div className="py-8 text-center flex flex-col items-center">
              <FiActivity className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm opacity-70">No weekly data available for this trainee.</p>
            </div>
          ) : (
            <div className="space-y-4 relative">
              {/* Timeline connector */}
              <div className="absolute left-3 top-4 bottom-4 w-0.5 bg-secondary-light/30 dark:bg-secondary-dark/30 z-0 hidden sm:block"></div>
              
              {weeks.map((wk) => {
                const weekEnd = endOfWeek(wk, { weekStartsOn: 1 });
                const isSel = selected && +selected === +wk;
                const count = reports.filter(r => r.reportDate >= wk && r.reportDate <= weekEnd).length;
                
                // Count report types for this week
                const weekDORs = reports.filter(r => r.reportDate >= wk && r.reportDate <= weekEnd && r.reportType === 'DOR').length;
                const weekEOWRs = reports.filter(r => r.reportDate >= wk && r.reportDate <= weekEnd && r.reportType === 'EndOfWeekReport').length;
                const weekOSRs = reports.filter(r => r.reportDate >= wk && r.reportDate <= weekEnd && r.reportType === 'OfficerSafetyReport').length;

                // Activity bar - shows relative distribution of reports
                const totalReports = weekDORs + weekEOWRs + weekOSRs;
                const dorPercent = totalReports > 0 ? (weekDORs / totalReports) * 100 : 0;
                const eowrPercent = totalReports > 0 ? (weekEOWRs / totalReports) * 100 : 0;
                const osrPercent = totalReports > 0 ? (weekOSRs / totalReports) * 100 : 0;

                return (
                  <div 
                    key={wk.toISOString()} 
                    className="transition-all duration-300 relative z-10"
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-3 top-3 w-2.5 h-2.5 rounded-full bg-accent-light dark:bg-accent-dark transform -translate-x-1/2 hidden sm:block" />
                    
                    <button
                      onClick={() => setSel(isSel ? null : wk)}
                      className={`w-full flex justify-between items-center p-3 rounded-lg text-left shadow-md transition-all duration-300 hover:shadow-lg sm:pl-8
                        ${isSel
                          ? 'bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark transform translate-y-0 scale-[1.01]'
                          : 'bg-secondary-light/60 dark:bg-secondary-dark/60 hover:bg-accent-light/90 hover:text-primary-light dark:hover:bg-accent-dark/90 dark:hover:text-primary-dark hover:-translate-y-0.5'
                        }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                        <div className="flex items-center">
                          <FiCalendar className="mr-2 flex-shrink-0 text-accent-light/80 dark:text-accent-dark/80" />
                          <span className="font-medium text-[10px] sm:text-sm">
                            {format(wk, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                          </span>
                        </div>
                        <div className="flex flex-row items-center ml-0 sm:ml-2 gap-1 flex-wrap mt-1 sm:mt-0">
                          {weekDORs > 0 && (
                            <span className="inline-flex items-center h-4 px-1.5 py-0 text-[8px] sm:text-[9px] sm:h-5 sm:px-2 rounded report-badge-dor border report-border-dor">
                              <span className="w-1.5 h-1.5 rounded-full report-bar-dor mr-0.5 sm:mr-1 flex-shrink-0"></span>
                              {weekDORs}
                            </span>
                          )}
                          {weekEOWRs > 0 && (
                            <span className="inline-flex items-center h-4 px-1.5 py-0 text-[8px] sm:text-[9px] sm:h-5 sm:px-2 rounded report-badge-eowr border report-border-eowr">
                              <span className="w-1.5 h-1.5 rounded-full report-bar-eowr mr-0.5 sm:mr-1 flex-shrink-0"></span>
                              {weekEOWRs}
                            </span>
                          )}
                          {weekOSRs > 0 && (
                            <span className="inline-flex items-center h-4 px-1.5 py-0 text-[8px] sm:text-[9px] sm:h-5 sm:px-2 rounded report-badge-osr border report-border-osr">
                              <span className="w-1.5 h-1.5 rounded-full report-bar-osr mr-0.5 sm:mr-1 flex-shrink-0"></span>
                              {weekOSRs}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-primary-light dark:bg-primary-dark text-[10px] font-semibold border border-secondary-light/40 dark:border-secondary-dark/40 text-accent-light dark:text-accent-dark mr-1">
                            {count}
                          </div>
                          {isSel ? <FiArrowUp className="transition-transform duration-300 transform" /> : <FiArrowDown className="transition-transform duration-300 transform" />}
                        </div>
                        
                          {totalReports > 0 && (
                            <div className="w-16 h-1.5 rounded-full bg-secondary-light/30 dark:bg-secondary-dark/30 flex overflow-hidden">
                              {weekDORs > 0 && <div className="h-full report-bar-dor" style={{ width: `${dorPercent}%` }}></div>}
                              {weekEOWRs > 0 && <div className="h-full report-bar-eowr" style={{ width: `${eowrPercent}%` }}></div>}
                              {weekOSRs > 0 && <div className="h-full report-bar-osr" style={{ width: `${osrPercent}%` }}></div>}
                            </div>
                          )}
                      </div>
                    </button>

                    <AnimatePresence>
                      {isSel && weekReports.length > 0 && (
                        <motion.div 
                          className="mt-4"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {weekReports.map((r) => {
                              const colour = `${shadeClasses[r.reportType]} ${colourClasses[r.reportType]}`;
                              const dispDate = toDate(r.submissionDate) || toDate(r.createdAt) || r.reportDate;

                              return (
                                <div 
                                  key={r.id} 
                                  className={`${colour} rounded-lg shadow-md border transition-all duration-200 hover:shadow-lg hover:translate-y-[-2px] overflow-hidden flex flex-col`}
                                >
                                  {/* Trainee indicator - colored top bar */}
                                  <div className="w-full h-1.5 bg-accent-light dark:bg-accent-dark"></div>
                                  
                                  {/* Card content with padding */}
                                  <div className="p-3 flex-1">
                                    {/* Report type badge */}
                                    <div className="flex justify-between items-start mb-3">
                                      <div className={`text-[8px] font-medium rounded px-1.5 py-0.5 ${reportTypeBadges[r.reportType]}`}>
                                        {r.reportType === 'DOR' ? 'Daily Report' : 
                                         r.reportType === 'EndOfWeekReport' ? 'Weekly Report' : 
                                         'Safety Report'}
                                      </div>
                                      
                                      <ReportIcon type={r.reportType} />
                                    </div>

                                    <div className="space-y-1.5">
                                      <p className="flex items-center text-[9px]">
                                        <FiEdit2 className="mr-1 w-2.5 h-2.5 text-text-light/70 dark:text-text-dark/70" />
                                        <span className="font-medium">{creatorNames[r.createdBy] || 'Unknown'}</span>
                                      </p>
                                      {/* Report Date */}
                                      {r.reportDate && (
                                        <p className="flex items-center text-[9px]">
                                          <FiCalendar className="mr-1 w-2.5 h-2.5 text-text-light/70 dark:text-text-dark/70" />
                                          <span>Report Date: {format(toDate(r.reportDate), 'MMM d, yyyy')}</span>
                                        </p>
                                      )}
                                      <p className="flex items-center text-[9px]">
                                        <FiClock className="mr-1 w-2.5 h-2.5 text-text-light/70 dark:text-text-dark/70" />
                                        <span>Submitted: {fmtDateTime(dispDate)}</span>
                                      </p>
                                    </div>

                                    <button
                                      onClick={() => {
                                        const base = {
                                          DOR: '/dor/',
                                          EndOfWeekReport: '/endofweekreport/',
                                          OfficerSafetyReport: '/officersafetyreport/',
                                        }[r.reportType];
                                        nav(base + r.id);
                                      }}
                                      className="mt-3 w-full flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 text-[9px] transition-all duration-200"
                                    >
                                      <FiCheckCircle className="w-2.5 h-2.5" /> View report
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}

                      {isSel && weekReports.length === 0 && (
                        <motion.div 
                          className="mt-4 p-6 text-center bg-secondary-light/30 dark:bg-secondary-dark/30 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <p className="text-sm">No reports available for this week.</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Add this CSS to your index.css or a relevant CSS file
// @keyframes fadeDown {
//   from { opacity: 0; transform: translateY(-10px); }
//   to { opacity: 1; transform: translateY(0); }
// }
// .animate-fadeDown {
//   animation: fadeDown 0.3s ease-out forwards;
// }
